'use client';

import { Button, CircularProgress, FormControlLabel, InputAdornment, Switch } from '@mui/material';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { useLazyQuery } from '@parkchirp/api-client';
import { FC, useCallback, useState } from 'react';
import { GET_TAX_REPORT } from '../../queries';
import type { SetShowSnackbar } from '../../types';
import { Dayjs } from '../../utils';

export const TaxReportForm: FC<{
	facilityId: string;
	setShowSnackbar: SetShowSnackbar;
}> = ({ facilityId, setShowSnackbar }) => {
	const [hideZeroDollarColumns, setHideZeroDollarColumns] = useState(true);
	const [accountId, setAccountId] = useState('');
	const [subscriptionName, setSubscriptionName] = useState('');
	const [transactionStartDate, setTransactionStartDate] = useState<Dayjs | null>(null);
	const [transactionEndDate, setTransactionEndDate] = useState<Dayjs | null>(null);
	const [applicationStartDate, setApplicationStartDate] = useState<Dayjs | null>(null);
	const [applicationEndDate, setApplicationEndDate] = useState<Dayjs | null>(null);
	const [isDownloadingCsv, setIsDownloadingCsv] = useState(false);
	const [getTaxReport] = useLazyQuery(GET_TAX_REPORT);

	const getDownloadParams = useCallback(
		() => ({
			facilityId,
			hideZeroDollarColumns,
			...(accountId && { accountId }),
			...(subscriptionName && { subscriptionName }),
			...(transactionStartDate && { transactionStartDate: transactionStartDate.startOf('day') }),
			...(transactionEndDate && { transactionEndDate: transactionEndDate.endOf('day') }),
			...(applicationStartDate && {
				applicationStartDate: applicationStartDate.startOf('day').format('YYYY-MM-DD'),
			}),
			...(applicationEndDate && { applicationEndDate: applicationEndDate.endOf('day').format('YYYY-MM-DD') }),
		}),
		[
			facilityId,
			hideZeroDollarColumns,
			accountId,
			subscriptionName,
			transactionStartDate,
			transactionEndDate,
			applicationStartDate,
			applicationEndDate,
		],
	);

	const params = getDownloadParams();

	const handleValidation = useCallback(() => {
		const validations = [
			{
				message: ' Transaction start time should be before transaction end time.',
				failingCondition:
					transactionStartDate &&
					transactionEndDate &&
					transactionEndDate.valueOf() < transactionStartDate.valueOf(),
			},
			{
				message: ' Application start time should be before application end time.',
				failingCondition:
					applicationStartDate &&
					applicationEndDate &&
					applicationEndDate.valueOf() < applicationStartDate.valueOf(),
			},
		];
		const failingValidations = validations.filter(({ failingCondition }) => failingCondition);
		if (failingValidations.length) {
			setShowSnackbar({
				message: failingValidations[0].message,
				severity: 'error',
				onClose: () => {
					setShowSnackbar(null);
				},
			});
			setIsDownloadingCsv(false);
			return false;
		}
		return true;
	}, [setShowSnackbar, transactionStartDate, transactionEndDate, applicationStartDate, applicationEndDate]);

	const handleReset = useCallback(() => {
		setAccountId('');
		setSubscriptionName('');
		setTransactionStartDate(null);
		setTransactionEndDate(null);
		setApplicationStartDate(null);
		setApplicationEndDate(null);
		setHideZeroDollarColumns(true);
	}, []);

	const handleDownload = useCallback(async () => {
		setIsDownloadingCsv(true);
		const validationSuccessful = handleValidation();
		if (validationSuccessful) {
			const { error } = await getTaxReport({
				context: {
					apiVersion: 'v2',
					appName: process.env.NEXT_PUBLIC_APP_NAME,
				},
				variables: params,
				onCompleted: (data: { getTaxReport: { url: string } }) => {
					const csv = document.createElement('a');
					csv.href = data.getTaxReport.url;
					csv.click();
				},
			});
			if (error) {
				setShowSnackbar({
					message: error.message,
					severity: 'error',
					onClose: () => {
						setShowSnackbar(null);
					},
				});
			}
			setIsDownloadingCsv(false);
		}
	}, [setShowSnackbar, getTaxReport, handleValidation, params]);

	return (
		<Box sx={{ padding: 3 }}>
			<Grid container spacing={2} sx={{ marginBottom: (theme) => theme.spacing(3) }}>
				<Grid item xs={6}>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<DatePicker
							label='Transaction Start Date'
							value={transactionStartDate}
							slotProps={{
								textField: {
									fullWidth: true,
									inputProps: { 'data-cy': 'transaction-start-date-picker' },
								},
							}}
							onChange={(e) => {
								setTransactionStartDate(e);
							}}
						/>
					</LocalizationProvider>
				</Grid>
				<Grid item xs={6}>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<DatePicker
							label='Transaction End Date'
							value={transactionEndDate}
							slotProps={{
								textField: {
									fullWidth: true,
									inputProps: { 'data-cy': 'transaction-end-date-picker' },
								},
							}}
							onChange={(e) => {
								setTransactionEndDate(e);
							}}
						/>
					</LocalizationProvider>
				</Grid>
			</Grid>
			<Grid container spacing={2} sx={{ marginBottom: (theme) => theme.spacing(3) }}>
				<Grid item xs={6}>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<DatePicker
							label='Application Start Date'
							value={applicationStartDate}
							slotProps={{
								textField: {
									fullWidth: true,
									inputProps: { 'data-cy': 'application-start-date-picker' },
								},
							}}
							onChange={(e) => {
								setApplicationStartDate(e);
							}}
						/>
					</LocalizationProvider>
				</Grid>
				<Grid item xs={6}>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<DatePicker
							label='Application End Date'
							value={applicationEndDate}
							slotProps={{
								textField: {
									fullWidth: true,
									inputProps: { 'data-cy': 'application-end-date-picker' },
								},
							}}
							onChange={(e) => {
								setApplicationEndDate(e);
							}}
						/>
					</LocalizationProvider>
				</Grid>
			</Grid>
			<Grid container spacing={2} sx={{ marginBottom: (theme) => theme.spacing(3) }}>
				<Grid item xs={6}>
					<TextField
						type='number'
						fullWidth
						variant='outlined'
						label='Account Id'
						value={accountId}
						onChange={(e) => {
							setAccountId(e.target.value);
						}}
						inputProps={{ 'data-cy': 'account-id-input' }}
						onKeyDown={(event) => {
							if (event?.key === '-' || event?.key === '+' || event?.key === 'e') {
								event.preventDefault();
							}
						}}
						onPaste={(event) => {
							event.preventDefault();

							const paste = event.clipboardData.getData('text');
							const cleanedPaste = /^[AC]*[-]*(\d+)$/.exec(paste);

							if (cleanedPaste) {
								setAccountId(cleanedPaste[1]);
							}
						}}
						InputProps={{
							startAdornment: <InputAdornment position='start'>AC-</InputAdornment>,
						}}
					/>
				</Grid>
				<Grid item xs={6}>
					<TextField
						fullWidth
						variant='outlined'
						label='Subscription Name'
						value={subscriptionName}
						onChange={(e) => {
							setSubscriptionName(e.target.value);
						}}
						inputProps={{ 'data-cy': 'subscription-name-input' }}
					/>
				</Grid>
			</Grid>
			<Grid container spacing={2}>
				<FormControlLabel
					value={hideZeroDollarColumns}
					labelPlacement='start'
					label='Hide Zero Dollar Columns: '
					control={
						<Switch
							checked={hideZeroDollarColumns}
							onChange={(e) => setHideZeroDollarColumns(e.target.checked)}
							inputProps={{ 'aria-label': 'controlled' }}
							data-cy='hide-zero-dollar-columns-switch'
						/>
					}
				/>
			</Grid>
			<Grid container spacing={2} sx={{ display: 'flex', justifyContent: 'center', alignSelf: 'center', mt: 3 }}>
				<Grid item>
					<Button
						variant='contained'
						size='small'
						onClick={handleDownload}
						disabled={!facilityId || isDownloadingCsv}
						data-cy='download-csv-button'
					>
						{isDownloadingCsv ? <CircularProgress size={24} /> : 'Download CSV'}
					</Button>
				</Grid>
				<Grid item>
					<Button
						variant='contained'
						size='small'
						fullWidth
						color='secondary'
						onClick={handleReset}
						data-cy='reset-button'
					>
						Reset
					</Button>
				</Grid>
			</Grid>
		</Box>
	);
};
